const REPORTED_FLAG = '__reportedToUser';
const HANDLED_FLAG = '__handledByCaller';

/**
 * Reads the payload of a failed request. Endpoints serving files answer as an
 * ArrayBuffer even when the body turns out to be the usual JSON error, so it is
 * decoded instead of being dropped as unreadable.
 *
 * @param {Error} error
 * @returns {*}
 */
const errorPayload = error => {
	const payload = error.response?.data;

	if (payload instanceof ArrayBuffer || ArrayBuffer.isView(payload)) {
		try {
			return JSON.parse(new TextDecoder().decode(payload));
		} catch (e) {
			return undefined;
		}
	}

	return payload;
};

/**
 * Builds a readable message out of a failed API call. Laravel returns validation
 * details as an object keyed by field name, so those are flattened into a single
 * sentence instead of being rendered as [object Object]. Server failures carry a
 * short code pointing at the place that broke, which is appended so the user can
 * quote it when reporting the problem.
 *
 * Responses that never reach PHP (WAF body-size limit, gateway errors) have no
 * JSON message, so the HTTP status is the only handle the user can quote.
 *
 * @param {Error} error
 * @returns {string}
 */
export const apiErrorMessage = error => {
	if (error?.response === undefined) return 'Brak połączenia z serwerem. Sprawdź połączenie i spróbuj ponownie.';

	const status = error.response.status;
	const payload = errorPayload(error);
	const message = payload?.message ?? payload?.error;
	const reference = typeof payload?.ref === 'string' ? ` (${payload.ref})` : '';

	if (typeof message === 'string' && message.trim()) return message.trim() + reference;

	if (message && typeof message === 'object') {
		const details = Object.values(message).flat().filter(item => typeof item === 'string');
		if (details.length) return details.join(' ');
	}

	const reportToAdmin = 'Prosimy o zgłoszenie z dokładnym opisem kiedy wystąpił problem.';

	if (status === 413) {
		return `Żądanie jest zbyt duże (HTTP ${status}). ${reportToAdmin}`;
	}

	const bodyText = typeof payload === 'string' ? payload : '';
	if (status === 403 || status === 406 || /modsecurity/i.test(bodyText)) {
		return `Żądanie zostało zablokowane przez zaporę serwera (HTTP ${status}). ${reportToAdmin}`;
	}

	if (status === 502 || status === 504) {
		return `Serwer pośredniczący nie odpowiedział (HTTP ${status}). ${reportToAdmin}`;
	}

	const statusLabel = status ? ` (HTTP ${status})` : '';
	return `Wystąpił nieoczekiwany błąd${statusLabel}. ${reportToAdmin}` + reference;
};

/**
 * Message for an error the caller shows on its own. Takes reporting over from the
 * global handler, so the same reason does not appear as a toast as well.
 *
 * @param {Error} error
 * @returns {string}
 */
export const handledErrorMessage = error => {
	markErrorHandled(error);

	return apiErrorMessage(error);
};

/**
 * Marks an error as already shown to the user, so it does not get reported twice.
 *
 * @param {Error} error
 */
export const markErrorReported = error => {
	if (error && typeof error === 'object') error[REPORTED_FLAG] = true;
};

/**
 * @param {*} error
 * @returns {boolean}
 */
export const isErrorReported = error => Boolean(error?.[REPORTED_FLAG]);

/**
 * Marks an error as reported by the call site itself.
 *
 * @param {Error} error
 */
export const markErrorHandled = error => {
	if (error && typeof error === 'object') error[HANDLED_FLAG] = true;
};

/**
 * @param {*} error
 * @returns {boolean}
 */
export const isErrorHandled = error => Boolean(error?.[HANDLED_FLAG]);
